import { useRef, useState } from "react";
import {
    getBuiltInPresets,
    exportPreset,
    parseImportedPreset,
} from "../../utils/presets";

interface PresetSelectorProps {
    toolId: string;
    onApply: (values: Record<string, unknown>) => void;
    onExport: () => Record<string, unknown>;
}

export function PresetSelector({
    toolId,
    onApply,
    onExport,
}: PresetSelectorProps) {
    const builtIn = getBuiltInPresets(toolId);
    const [selected, setSelected] = useState("");
    const fileRef = useRef<HTMLInputElement>(null);

    const handleSelect = (idx: string) => {
        setSelected(idx);
        if (idx === "") return;
        const preset = builtIn[Number(idx)];
        if (preset) {
            onApply(preset.values);
            setTimeout(() => setSelected(""), 0);
        }
    };

    const handleExport = () => {
        const name = window.prompt("Preset name:");
        if (!name) return;
        const description =
            window.prompt("Description (optional):") || undefined;
        const values = onExport();
        exportPreset({ name, description, values });
    };

    const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const preset = parseImportedPreset(reader.result as string);
                onApply(preset.values);
            } catch (err) {
                alert(
                    err instanceof Error
                        ? err.message
                        : "Failed to import preset.",
                );
            }
        };
        reader.readAsText(file);
        if (fileRef.current) fileRef.current.value = "";
    };

    return (
        <div className="tool-input-group">
            <label
                className="field-label"
                style={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    display: "block",
                }}
            >
                Presets
            </label>
            {builtIn.length > 0 && (
                <div className="field-row">
                    <label className="field-label">
                        Built-in
                        <select
                            value={selected}
                            onChange={(e) => handleSelect(e.target.value)}
                            style={{ marginTop: "4px", width: "100%" }}
                        >
                            <option value="">- select preset -</option>
                            {builtIn.map((p, i) => (
                                <option key={i} value={String(i)}>
                                    {p.name}
                                    {p.description ? ` - ${p.description}` : ""}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            )}
            <div
                className="field-row"
                style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
            >
                <button
                    type="button"
                    className="generic-button"
                    onClick={handleExport}
                >
                    Export preset
                </button>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => fileRef.current?.click()}
                >
                    Import preset
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept=".json"
                    onChange={handleImport}
                    style={{ display: "none" }}
                />
            </div>
        </div>
    );
}
